import MainView from "views/MainView";

import { useEffect, useRef, useState } from "react";
import {
  _H5Event,
  handleFromAppParams,
  productManual,
  publicPath,
} from "request";
import { useIntlContext, SupportedLocales } from "views/IntlContext";

import { ManualCard, PdfViewer } from "./components";
import { Spinner } from "components";
import { FormattedMessage } from "react-intl";

interface ManualData {
  [key: string]: string | number | null;
  installUrl: string;
  installTitle: string;
  partsUrl: string;
  partsTitle: string;
  productUrl: string;
  productTitle: string;
  saleUrl: string;
  saleTitle: string;
  useUrl: string;
  useTitle: string;
  content: string;
}

const manualDataDefault = {
  installUrl: "",
  installTitle: "",
  partsUrl: "",
  partsTitle: "",
  productUrl: "",
  productTitle: "",
  saleUrl: "",
  saleTitle: "",
  useUrl: "",
  useTitle: "",
  content: "",
};

export const ProductManual = () => {
  const { switchLanguage } = useIntlContext();
  const [manual, setManual] = useState<ManualData>(manualDataDefault);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isEmpty = useRef<boolean | undefined>(false);
  const pdfUrl = useRef<string | undefined>(undefined);

  const onGetProductManual = (data: any) => {
    setIsLoading(true);
    productManual(data)
      .then((response: any) => {
        setIsLoading(false);
        if (response?.code === "200") {
           const handleFields: Array<keyof ManualData> = [
             "installUrl",
             "installTitle",
             "partsUrl",
             "partsTitle",
             "productUrl",
             "productTitle",
             "saleUrl",
             "saleTitle",
             "useUrl",
             "useTitle",
             "content",
           ];

           const filteredManual: ManualData = Object.keys(
             response.result
           ).reduce((acc, key) => {
             if (handleFields.includes(key as keyof ManualData)) {
               acc[key] = response.result[key as keyof typeof response.result];
             }
             return acc;
           }, {} as ManualData);

           const customResult = Object.keys(filteredManual)
             .filter((key) => handleFields.includes(key))
             .map((key) => {
               const value = filteredManual[key];
               if (typeof value === "string") {
                 return value.trim() !== "" && value !== null;
               }
               return false;
             });

           if (filteredManual.content) {
             const hrefs = filteredManual?.content.match(
               /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1[^>]*>(.*?)<\/a>/g
             );
             let pdfHrefs = [];
             if (hrefs && hrefs.length) {
               for (let i = 0; i < hrefs.length; i++) {
                 const match = hrefs[i].match(
                   /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1[^>]*>(.*?)<\/a>/
                 );
                 if (match) {
                   const url = match[2];
                   if (url.toLowerCase().indexOf(".pdf") !== -1) {
                     pdfHrefs.push(url);
                   }
                 }
               }
             }
             if (pdfHrefs.length > 0) {
               pdfUrl.current = pdfHrefs[0];
             }
           }
           isEmpty.current = !customResult.some(Boolean);
           setManual(filteredManual);
        }
        console.log(response);
      })
      .catch((err: any) => {
        setIsLoading(false);
        console.log(err);
      });
  };

  useEffect(() => {
    const datas = {
      event: "GetInstructionInitParams",
      pageName: "instructions",
      callback: (responseStr: any) => {
        const jsonData = handleFromAppParams(responseStr);
        switchLanguage?.(jsonData.lang as SupportedLocales);
        onGetProductManual(jsonData);
      },
    };
    _H5Event(datas);
  }, []);

  return (
    <MainView backgroundClassName="bg-page dark:bg-black1b1f1e pt-3">
      <div className="w-full h-full max-w-[650px] mx-auto">
        {isLoading && (
          <div className="flex items-center flex-col justify-center h-3/5">
            <Spinner
              extraSmall
              className={{ "mt-12": true, "border-gray999": true }}
            />
          </div>
        )}
        {isEmpty.current === true && (
          <div className="flex items-center flex-col justify-center h-3/5">
            <img
              src={`${publicPath}/icons/img_nodata.png`}
              className="w-[108px]"
              alt="empty"
            />
            <p className="mt-6 text-gray999">
              <FormattedMessage id="product.manual.empty.text" />
            </p>
          </div>
        )}

        {isEmpty.current === false && pdfUrl.current && (
          <PdfViewer pdfUrl={pdfUrl.current} />
        )}

        {!pdfUrl.current && isEmpty.current === false && (
          <div className="bg-white">
            <ManualCard
              videoUrl={manual?.installUrl}
              description={manual?.installTitle}
            />
            <ManualCard
              videoUrl={manual?.partsUrl}
              description={manual?.partsTitle}
            />
            <ManualCard
              videoUrl={manual?.productUrl}
              description={manual?.productTitle}
            />
            <ManualCard
              videoUrl={manual?.saleUrl}
              description={manual?.saleTitle}
            />
            <ManualCard
              videoUrl={manual?.useUrl}
              description={manual?.useTitle}
            />
            {manual?.content && (
              <div
                className="px-6 max-w-[360px]"
                dangerouslySetInnerHTML={{ __html: manual?.content }}
              />
            )}
          </div>
        )}
      </div>
    </MainView>
  );
};
